/*
  geo-popup.js: the launch sign-up pop-up (sections/geo-popup.liquid).

  Replaced geo-redirect.js on 14 Oct 2026. US visitors used to be sent from the
  store locator to /pages/us-coming-soon; the client now wants them to keep
  browsing the international site and be invited to join the US launch list.

  The country comes from window.GrittGeo (snippets/geo-country.liquid), Shopify's
  own IP lookup, shared with the sign-up and contact forms.

  It opens after the section's delay when the country is listed, unless the
  visitor closed it within the section's "show again" days or already signed
  up. The ?geo= parameter kept its meaning from the redirect:
      ?geo=off   no pop-up for 30 days (the coming-soon page's link adds this)
      ?geo=on    undo that and forget a closed pop-up
      ?geo=US    open it now from any IP, for testing

  The form submits normally, so Shopify's spam check (hCaptcha plus a form_key it
  adds on submit) runs untouched; a fetch POST without them gets Shopify's 403
  "Verifying your connection" page. Shopify's form action ends in #<form id> and
  the browser keeps that fragment through the redirect back, so the hash tells
  this script to reopen the pop-up, where Liquid has rendered the result.

  The dialog opens with show(), not showModal(): a modal dialog makes everything
  outside it inert and outranks every z-index, so the hCaptcha challenge Shopify
  appends to <body> could not be seen or clicked. This script does what the modal
  did: the page behind goes inert, Esc closes, Tab loops inside the panel.
*/

const DAY = 86400;
const BYPASS = 'gritt_geo_bypass'; // ?geo=off
const STATE = 'gritt_geo_popup'; // 'closed' (for N days) or 'joined'

const designMode = Boolean(window.Shopify && window.Shopify.designMode);

function cookie(name) {
  const hit = document.cookie.match('(^|; )' + name + '=([^;]*)');
  return hit ? decodeURIComponent(hit[2]) : null;
}

function setCookie(name, value, maxAge) {
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

const FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

let inerted = [];
let returnFocus = null;

function open(dialog) {
  if (dialog.open) return;
  returnFocus = document.activeElement;
  dialog.show();
  // The theme's own page blocks only: hCaptcha and Shopify's privacy banner are
  // also children of <body> and must stay usable.
  inerted = [...document.body.children].filter(
    (el) => !el.contains(dialog) && el.matches('.shopify-section, main, .skip-link')
  );
  inerted.forEach((el) => { el.inert = true; });
  // Focus the dialog itself: focusing the first control (the X) would show the red focus ring on open.
  dialog.focus();
  document.documentElement.classList.add('geo-popup-open');
}

function bind(dialog) {
  const snoozeDays = Number(dialog.dataset.snoozeDays) || 7;

  dialog.querySelectorAll('[data-geo-popup-close]').forEach((button) => {
    button.addEventListener('click', () => dialog.close());
  });
  // The dialog is the dimmed backdrop; the panel sits inside it.
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) dialog.close();
  });
  document.addEventListener('keydown', (e) => {
    if (!dialog.open || !dialog.isConnected) return;
    if (e.key === 'Escape') {
      dialog.close();
      return;
    }
    // Loop Tab inside the panel. Focus outside it means hCaptcha or the privacy banner: leave that alone.
    const active = document.activeElement;
    if (e.key !== 'Tab' || !dialog.contains(active)) return;
    const items = [...dialog.querySelectorAll(FOCUSABLE)].filter((el) => !el.disabled && el.offsetParent);
    const first = items[0];
    const last = items[items.length - 1];
    if (!first) return;
    if (e.shiftKey && (active === first || active === dialog)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  });
  // Covers every way out: the X, the stay link, Esc and the backdrop.
  dialog.addEventListener('close', () => {
    inerted.forEach((el) => { el.inert = false; });
    inerted = [];
    document.documentElement.classList.remove('geo-popup-open');
    if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
    if (!designMode && cookie(STATE) !== 'joined') setCookie(STATE, 'closed', snoozeDays * DAY);
  });
}

function start(dialog) {
  bind(dialog);

  const countries = (dialog.dataset.countries || '')
    .split(',')
    .map((c) => c.trim().toUpperCase())
    .filter(Boolean);
  const delay = (Number(dialog.dataset.delay) || 0) * 1000;
  const params = new URLSearchParams(location.search);

  // Back from this pop-up's sign-up (the footer form has its own id): show the result.
  if (location.hash === '#' + dialog.dataset.form) {
    if (params.get('customer_posted') === 'true') setCookie(STATE, 'joined', 365 * DAY);
    open(dialog);
    return;
  }

  const override = (params.get('geo') || '').trim().toUpperCase();
  if (override === 'OFF') {
    setCookie(BYPASS, '1', 30 * DAY);
    return;
  }
  if (override === 'ON') {
    setCookie(BYPASS, '', 0);
    setCookie(STATE, '', 0);
  } else if (override) {
    if (countries.includes(override)) open(dialog);
    return;
  }

  if (cookie(BYPASS) || cookie(STATE)) return;

  window.GrittGeo.country().then((country) => {
    if (country && countries.includes(country)) setTimeout(() => open(dialog), delay);
  });
}

const dialog = document.querySelector('[data-geo-popup]');

if (designMode) {
  // Theme editor: no geo logic, the pop-up opens while its section is selected.
  const find = (e) => e.target.querySelector?.('[data-geo-popup]');
  if (dialog) bind(dialog);
  document.addEventListener('shopify:section:load', (e) => {
    const fresh = find(e);
    if (fresh) { bind(fresh); open(fresh); }
  });
  document.addEventListener('shopify:section:select', (e) => { const d = find(e); if (d) open(d); });
  document.addEventListener('shopify:section:deselect', (e) => { const d = find(e); if (d) d.close(); });
} else if (dialog) {
  start(dialog);
}
